import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import type { EasyModeRouteParams } from './easyModeNavigation';

const PENDING_EASY_MODE_DEEP_LINK_KEY = 'smartsaas_pending_easy_mode_deep_link';

export type PendingEasyModeDeepLink = {
  /** Open Easy Mode even when no prompt was provided (e.g. /help) */
  openEasyMode: true;
  initialUserPrompt?: string;
  initialPromptNonce?: number;
  /** `marketing`: business idea from the signed-out `/?prompt=` link (routes to the guest preview). */
  source?: 'marketing';
};

const PROMPT_QUERY_KEYS = ['prompt', 'q', 'text', 'message'] as const;

/** Avoid URLSearchParams — Expo jest winter polyfill breaks it. */
function readQueryParam(queryPart: string, keys: readonly string[]): string | undefined {
  if (!queryPart) return undefined;
  const pairs = queryPart.split('&');
  for (const key of keys) {
    for (const pair of pairs) {
      if (!pair) continue;
      const eq = pair.indexOf('=');
      const rawKey = eq === -1 ? pair : pair.slice(0, eq);
      const rawVal = eq === -1 ? '' : pair.slice(eq + 1);
      let decodedKey = rawKey;
      let decodedVal = rawVal;
      try {
        decodedKey = decodeURIComponent(rawKey.replace(/\+/g, ' '));
        decodedVal = decodeURIComponent(rawVal.replace(/\+/g, ' '));
      } catch {
        /* keep raw */
      }
      if (decodedKey === key) {
        const trimmed = decodedVal.trim();
        if (trimmed) return trimmed;
      }
    }
  }
  return undefined;
}

/**
 * Paths that should open Easy Mode (AI help), including the /help alias.
 * Query: prompt | q | text | message
 */
export function isEasyModeHelpDeepLinkPath(path: string): boolean {
  if (typeof path !== 'string' || !path) return false;
  const rawPath = path.split('?')[0].replace(/\/+$/, '') || '/';
  return /(^|\/)(easy-mode|help)$/i.test(rawPath);
}

export function extractEasyModePromptFromPath(path: string): string | undefined {
  if (typeof path !== 'string' || !path.includes('?')) return undefined;
  const queryPart = path.split('?')[1] || '';
  return readQueryParam(queryPart, PROMPT_QUERY_KEYS);
}

/** Marketing "Build Your Business" lands on `/?prompt=` (or `/login?prompt=`); only `prompt` is honoured there. */
const MARKETING_PROMPT_PATHS = new Set(['', '/login']);

export function extractMarketingPromptFromPath(path: string): string | undefined {
  if (typeof path !== 'string') return undefined;
  const queryIndex = path.indexOf('?');
  if (queryIndex === -1) return undefined;
  const pathOnly = path.slice(0, queryIndex).replace(/\/+$/, '');
  if (!MARKETING_PROMPT_PATHS.has(pathOnly)) return undefined;
  return readQueryParam(path.slice(queryIndex + 1).split('#')[0], ['prompt']);
}

/** Stash `?prompt=` from the cold-load URL so it survives Loading → Login → sign-in. */
export function stashMarketingPromptFromPath(path: string): boolean {
  const prompt = extractMarketingPromptFromPath(path);
  if (!prompt) return false;
  stashPendingEasyModeDeepLink(prompt, 'marketing');
  return true;
}

export function buildEasyModeRouteParams(prompt?: string): EasyModeRouteParams | undefined {
  const trimmed = prompt?.trim();
  if (!trimmed) return undefined;
  return {
    initialUserPrompt: trimmed,
    initialPromptNonce: Date.now(),
  };
}

/** React Navigation state: root → Dashboard → EasyMode (+ optional prompt). */
export function buildEasyModeNavigationState(prompt?: string) {
  const params = buildEasyModeRouteParams(prompt);
  return {
    routes: [
      {
        name: 'Dashboard',
        state: {
          routes: [
            {
              name: 'EasyMode',
              ...(params ? { params } : {}),
            },
          ],
        },
      },
    ],
  };
}

export function stashPendingEasyModeDeepLink(
  prompt?: string,
  source?: PendingEasyModeDeepLink['source'],
): void {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  try {
    const payload: PendingEasyModeDeepLink = {
      openEasyMode: true,
      ...buildEasyModeRouteParams(prompt),
      ...(source ? { source } : {}),
    };
    sessionStorage.setItem(PENDING_EASY_MODE_DEEP_LINK_KEY, JSON.stringify(payload));
  } catch {
    /* best-effort */
  }
}

export function consumePendingEasyModeDeepLink(): PendingEasyModeDeepLink | null {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_EASY_MODE_DEEP_LINK_KEY);
    sessionStorage.removeItem(PENDING_EASY_MODE_DEEP_LINK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingEasyModeDeepLink;
    if (!parsed?.openEasyMode) return null;
    return parsed;
  } catch {
    return null;
  }
}

function peekPendingEasyModeDeepLink(): PendingEasyModeDeepLink | null {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_EASY_MODE_DEEP_LINK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingEasyModeDeepLink;
    return parsed?.openEasyMode ? parsed : null;
  } catch {
    return null;
  }
}

/** True when a stashed deep link carries a prompt (does not consume it). */
export function hasPendingEasyModePrompt(): boolean {
  return Boolean(peekPendingEasyModeDeepLink()?.initialUserPrompt?.trim());
}

/** Business idea from the marketing `?prompt=` link, if still pending (does not consume it). */
export function peekPendingMarketingPrompt(): string | undefined {
  const pending = peekPendingEasyModeDeepLink();
  if (pending?.source !== 'marketing') return undefined;
  return pending.initialUserPrompt?.trim() || undefined;
}

/** Replace the pending marketing idea (guest edited it on the preview screen). */
export function updatePendingMarketingPrompt(prompt: string): void {
  if (prompt.trim()) stashPendingEasyModeDeepLink(prompt, 'marketing');
}

/** App-scheme URL for Shortcuts / Watch / Siri handoff. */
export function createEasyModeHelpUrl(prompt?: string): string {
  const trimmed = prompt?.trim();
  if (trimmed) {
    return Linking.createURL('dashboard/easy-mode', {
      queryParams: { prompt: trimmed },
    });
  }
  return Linking.createURL('dashboard/easy-mode');
}
